import { jwtDecode } from "jwt-decode";
import { useUserType } from "../contexts/UserTypeContext";
import { getItem, setItem, removeItem } from "./storage";

const DEFAULT_TITLE = 'Contract Management System';
const DONOR_TITLE = 'Donor Management System';

export const setPageTitle = (title) => {
  try {
    const hostname = window?.location?.hostname;
    const isDonor = hostname?.includes("clouddonormanager");
    const baseTitle = isDonor ? DONOR_TITLE : DEFAULT_TITLE;

    const pathname = window?.location?.pathname || '';
    const isSuperAdminRoute = pathname.startsWith('/superadmin');

    // Check if we're on auth pages (login, logout, signup, forgot password, etc.)
    const isAuthPage = pathname.includes('/auth/login') ||
                      pathname.includes('/auth/logout') ||
                      pathname.includes('/auth/signup') ||
                      pathname.includes('/auth/forgot-password') ||
                      pathname.includes('/auth/reset-password');

    if (isSuperAdminRoute) {
      document.title = title ? `${title} | ${baseTitle}` : baseTitle;
      return;
    }

    // On auth pages or when no token exists, don't show company name
    if (isAuthPage) {
      const pageTitle = getPageTitleFromRoute(pathname) || 'Login';
      document.title = title ? `${title} | ${baseTitle}` : `${pageTitle} | ${baseTitle}`;
      return;
    }

    // Check if user is authenticated (has any token)
    const hasToken = getItem('adminToken') ||
                    getItem('workerToken') ||
                    getItem('customerToken') ||
                    getItem('donorToken');

    // Only show company name if user is authenticated
    if (hasToken) {
      const companyName = localStorage.getItem('companyName') || baseTitle;
      document.title = title ? `${title} | ${companyName}` : companyName;
    } else {
      // No token, use base title
      document.title = title ? `${title} | ${baseTitle}` : baseTitle;
    }
  } catch (error) {
    console.error('Error setting page title:', error);
    const hostname = window?.location?.hostname;
    const isDonor = hostname?.includes("clouddonormanager");
    document.title = isDonor ? DONOR_TITLE : DEFAULT_TITLE;
  }
};

export const updateCompanyNameFromToken = async () => {
  try {
    const token = getItem('adminToken') ||
                 getItem('workerToken') ||
                 getItem('customerToken') ||
                 getItem('donorToken');

    if (!token) {
      // Remove company name if no token exists (logout/session expired)
      const pathname = window?.location?.pathname || '';
      const isAuthPage = pathname.includes('/auth/login') ||
                        pathname.includes('/auth/logout') ||
                        pathname.includes('/auth/signup') ||
                        pathname.includes('/auth/forgot-password') ||
                        pathname.includes('/auth/reset-password');
      if (isAuthPage) {
        localStorage.removeItem('companyName');
      }
      setPageTitle();
      return;
    }

    const pathname = window?.location?.pathname || '';
    const isSuperAdminRoute = pathname.startsWith('/superadmin');

    const isImpersonated = getItem('isImpersonated') === 'true';

    if (isSuperAdminRoute && !isImpersonated) {
      localStorage.removeItem('companyName'); // companyName is not auth-related, keep in localStorage
      setPageTitle();
      return;
    }

    // JOB-3244: decode CompanyName locally from the JWT instead of hitting
    // /auth/token_data. The server returns the same value that's already
    // encoded in the token; the blocking round-trip added 100–500ms to every
    // navigation.
    let companyName = null;
    try {
      const decoded = jwtDecode(token);
      companyName =
        decoded?.CompanyName ||
        decoded?.data?.CompanyName ||
        null;
    } catch (decodeErr) {
      companyName = null;
    }

    if (companyName) {
      if (!isSuperAdminRoute || isImpersonated) {
        localStorage.setItem('companyName', companyName); // companyName is not auth-related, keep in localStorage
      } else {
        localStorage.removeItem('companyName'); // companyName is not auth-related, keep in localStorage
      }
    } else if (isSuperAdminRoute && !isImpersonated) {
      localStorage.removeItem('companyName'); // companyName is not auth-related, keep in localStorage
    }
    setPageTitle();
  } catch (error) {
    console.error('Error updating company name:', error);
    setPageTitle();
  }
};

/** URL segments that are IDs, not page names — skip when deriving the browser
 * tab title so /edit-customer/<uuid> reads "Edit Customer" not the raw UUID. */
const ID_SHAPED_SEGMENT = new RegExp(
  "^(?:" +
    // UUID v1–v5 (8-4-4-4-12 hex)
    "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}" +
    "|" +
    // MongoDB ObjectId (24 hex)
    "[0-9a-f]{24}" +
    "|" +
    // Pure numeric id
    "\\d+" +
  ")$",
  "i"
);

/** Route-slug → human title overrides for cases where the default
 * split-and-capitalize logic produces ugly titles. JOB-3227:
 *   /pos              → "POS"         (was "Pos")
 *   /manageteam       → "Manage Team" (was "Manageteam")
 *   /materials-labor  → "Materials & Labor" (JOB-3229 — canonical kebab-case path)
 *   /setting          → "Settings"    (was "Setting" — plural everywhere else)
 * Keys are normalized lower-case. Extend as new ugly titles are found.
 */
const TITLE_OVERRIDES = {
  "pos": "POS",
  "manageteam": "Manage Team",
  "manage-team": "Manage Team",
  "materials&labor": "Materials & Labor",
  "materials-labor": "Materials & Labor",
  "setting": "Settings",
  "settings": "Settings",
  "signup": "Sign Up",
  "sign-up": "Sign Up",
  "superadminprofile": "Super Admin Profile",
  "firsttimenewpassword": "New Password",
  "invoicepayment": "Invoice Payment",
  "linkpayment": "Link Payment",
  "acceptdonation": "Accept Donation",
  "dispatch-board": "Dispatch Board",
  "time-off": "Time Off",
  "shift-requests": "Shift Requests",
  "worker-hours": "Worker Hours",
  // Tab title for /company-reports/labor-hours-report — matches sidebar
  // ("Worker Hours") and page H1 ("Worker Hours Report") despite the URL slug.
  "labor-hours-report": "Worker Hours Report",
  "email-templates": "Email Templates",
  "email-logs": "Email Logs",
  "email-campaign": "Email Campaign",
  "manage-templates": "Manage Templates",
  "customerdetails": "Customer Details",
  "contract-details": "Contract Details",
  "invoice-details": "Invoice Details",
  "invoice-detail": "Invoice Details",
  "quotes-detail": "Quote Details",
  "quote-details": "Quote Details",
  "service-requests": "Service Requests",
  "service-request-detail": "Service Request",
  // JOB-3264 — pluralize Superadmin list/detail tab titles
  "plan": "Plans",
  "company": "Companies",
  "company-detail": "Company Details",
  // JOB-3263 — Invoice list pages (customer/worker portals + admin) should be plural
  "invoice": "Invoices",
  "worker-invoice": "Invoices",
};

export const getPageTitleFromRoute = (pathname) => {
  if (!pathname) return '';

  const specialRoutes = {
    '/auth/login': 'Login',
    '/auth/logout': 'Login',
    '/auth/signup': 'Sign Up',
    '/auth/forgot-password': 'Forgot Password',
    '/auth/reset-password': 'Reset Password'
  };

  for (const [route, title] of Object.entries(specialRoutes)) {
    if (pathname.includes(route)) return title;
  }

  const segments = pathname.split('/').filter(Boolean);
  if (!segments.length) return '';

  // Walk the path from right to left and return the first segment that
  // isn't an ID-shaped parameter. Fixes the "edit-customer/<uuid>" title
  // bug where the UUID got split on hyphens and rendered as the title.
  let candidate = '';
  for (let i = segments.length - 1; i >= 0; i--) {
    if (!ID_SHAPED_SEGMENT.test(segments[i])) {
      candidate = segments[i];
      break;
    }
  }
  // If every segment is id-shaped (should be impossible for our routes),
  // fall back to the last segment so we still produce a title.
  if (!candidate) candidate = segments[segments.length - 1];

  // Route-name override for slugs that capitalize-split-on-hyphen doesn't
  // produce a nice title for (POS, Manage Team, Materials & Labor, etc.)
  const normalized = candidate.toLowerCase();
  if (TITLE_OVERRIDES[normalized]) {
    return TITLE_OVERRIDES[normalized];
  }

  return candidate
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const usePageTitle = () => {
  const { userType } = useUserType();
  return userType === 'donor' ? DONOR_TITLE : DEFAULT_TITLE;
};

export const updatePageContent = (userType) => {
  const isDonor = userType === 'donor';
  const title = isDonor ? DONOR_TITLE : DEFAULT_TITLE;
  const description = isDonor
    ? 'Donor Management System – Manage and streamline your donations efficiently'
    : 'Contract Management System – Manage and streamline your contracts efficiently';

  setPageTitle();

  const updateMetaElements = () => {
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', title);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', description);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateMetaElements);
  } else {
    updateMetaElements();
  }
};
